import { memo, useMemo, useCallback, useSyncExternalStore } from "react";
import { type ReactNode } from "react";

import { Transition } from "@headlessui/react";

import {
  STOREFRONT_TRANSITION_DURATION,
  STOREFRONT_TRANSITION_EASING,
} from "@root/tablescape/constants";
import {
  type TablescapeState,
  type GlobalDrawer,
} from "@root/tablescape/state";
import {
  TablescapeContext,
  TablescapeEvent,
} from "@root/tablescape/utilities/tablescapeContext";
import { tw } from "classnames";

const transitionAnimation = {
  backdrop: {
    enter: tw(
      "transition",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    enterFrom: tw("opacity-0"),
    enterTo: tw("opacity-80"),
    leave: tw(
      "transition",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    leaveFrom: tw("opacity-80"),
    leaveTo: tw("opacity-0"),
  },
  sidebar: {
    enter: tw(
      "transition-transform",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    enterFrom: tw("translate-x-full"),
    enterTo: tw("translate-x-0"),
    leave: tw(
      "transition-transform",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    leaveFrom: tw("translate-x-0"),
    leaveTo: tw("translate-x-full"),
  },
};

const styles = {
  container: tw("fixed", "inset-0", "z-max"),
  backdrop: tw("absolute", "inset-0", "bg-overlay", "z-10"),
  sidebar: tw(
    "absolute",
    "top-0",
    "left-0",
    "md:left-auto",
    "md:right-0",
    "h-full",
    "w-screen",
    "md:w-sidebar",
    "z-20"
  ),
};

type Props = {
  render: (props: { closeDrawer: () => void }) => ReactNode;
  target: GlobalDrawer;
};

const DrawerProviderBase = ({ render, target }: Props) => {
  const {
    ui: { globalDrawerTarget },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );

  const sidebarOpen = useMemo(
    () => globalDrawerTarget === target,
    [globalDrawerTarget, target]
  );

  const closeDrawer = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.TOGGLE_GLOBAL_DRAWER, {
      ui: {
        globalDrawerTarget: null,
      },
    });
  }, []);

  const memoizedDrawer = useMemo(
    () => render({ closeDrawer }),
    [closeDrawer, render]
  );

  return (
    <Transition className={styles.container} show={sidebarOpen}>
      {/* Backdrop */}
      <Transition.Child
        className={styles.backdrop}
        onClick={closeDrawer}
        {...transitionAnimation.backdrop}
      />

      {/* Drawer */}
      <Transition.Child
        className={styles.sidebar}
        {...transitionAnimation.sidebar}
      >
        {memoizedDrawer}
      </Transition.Child>
    </Transition>
  );
};

export const DrawerProvider = memo(DrawerProviderBase);
